import { type ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

export const ReferenceItemFragment = graphql(`
  fragment ReferenceItemFragment on ProfessionalReference {
    id
    status
    first_name
    last_name
    title
    department
    location
    start_date
    end_date
    current_position
    email
    phone_number
    created_at
    uuid
    work_history_id
    work_history {
      id
      organization_name
      unit
    }
  }
`);

export const WorkHistoriesFragment = graphql(`
  fragment WorkHistoriesFragment on WorkHistory {
    id
    organization_name
    unit
    start_date
    end_date
    currently_work_here
  }
`);

const ReferencesGql = graphql(`
  query GetProfessionalReferences {
    professionalReferences {
      id
      current_position
      end_date
      ...ReferenceItemFragment
    }
  }
`);

const ReferenceGql = graphql(`
  query GetProfessionalReferenceItem($id: Int!) {
    professionalReference(id: $id) {
      id
      status
      first_name
      last_name
      title
      department
      location
      start_date
      end_date
      current_position
      email
      phone_number
      created_at
      uuid
      work_history_id
      work_history {
        id
        organization_name
        unit
      }
      ...ReferenceItemFragment
    }
  }
`);

export const WorkHistoriesGql = graphql(`
  query GetWorkHistoriesForReferenceForm($input: WorkHistoryQueryInput) {
    workHistories(input: $input) {
      id
      organization_name
      unit
      clinical
      currently_work_here
      ...WorkHistoriesFragment
    }
  }
`);

type ReferencesQueryResult = ResultOf<typeof ReferencesGql>;

type ReferenceQueryResult = ResultOf<typeof ReferenceGql>;

export const useReferenceForViewPage = (id?: number | null) => {
  const { data, isLoading } = useGraphQLQuery<ReferenceQueryResult>({
    enabled: !!id,
    operationName: 'GetProfessionalReferenceItem',
    query: ReferenceGql,
    variables: { id },
  });

  const reference = data?.professionalReference ?? null;

  return { reference, isLoading };
};

export const useReferencesForMainPage = (): {
  references: ReferencesQueryResult['professionalReferences'];
  isLoading: boolean;
} => {
  const { data, isLoading } = useGraphQLQuery<ReferencesQueryResult>({
    operationName: 'GetProfessionalReferences',
    query: ReferencesGql,
    variables: {},
  });

  const references = data?.professionalReferences ?? [];

  return { references, isLoading };
};

export const useWorkHistoriesForReferenceForm = () => {
  const { data, isLoading, refetch } = useGraphQLQuery<ResultOf<typeof WorkHistoriesGql>>({
    operationName: 'GetWorkHistoriesForReferenceForm',
    query: WorkHistoriesGql,
    variables: {
      input: { orderBy: { start_date: false } },
    },
  });

  const workHistories = data?.workHistories || [];

  return {
    isLoading,
    workHistories,
    refetch,
  };
};
